import * as React from 'react'
import { createContainer } from 'unstated-next'

import { reportClientError } from '../../../../../../app/_components/chrome/scripts/DataDogRumScript'
import { Variant } from '../../../../../../types/graphql'

export type WaitlistRequestResult = 'success' | 'email-already-registered' | 'unexpected-error'

interface IProps
  extends Readonly<{
    currentColorKey: string | undefined
    productTitle: string | undefined
    selectedVariant: Variant | undefined
  }> {}

const defaultProps: IProps = {
  currentColorKey: undefined,
  productTitle: undefined,
  selectedVariant: undefined,
}

export interface ISendWaitlistRequestArgs
  extends Readonly<{
    email: string
    variantId: number
  }> {}

function useWaitlistService({
  currentColorKey,
  productTitle,
  selectedVariant,
}: IProps = defaultProps) {
  const [shouldDisplayWaitlistModal, setShouldDisplayWaitlistModal] = React.useState<boolean>(false)
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null)
  const [successMessage, setSuccessMessage] = React.useState<string | null>(null)

  function openWaitlistModal(): void {
    setShouldDisplayWaitlistModal(true)
  }

  function clearMessages(): void {
    setErrorMessage(null)
    setSuccessMessage(null)
  }

  function closeWaitlistModal(): void {
    clearMessages()
    setShouldDisplayWaitlistModal(false)
  }

  const sendWaitlistRequest = React.useCallback(
    async (args: ISendWaitlistRequestArgs): Promise<WaitlistRequestResult> => {
      clearMessages()
      try {
        const clientApiBaseUrl = process.env.NEXT_PUBLIC_CLIENT_API_BASE_URL
        const response = await fetch(`${clientApiBaseUrl}/api/waitlist/waitlists/product`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(args),
        })

        let waitlistRequestResult: WaitlistRequestResult = 'success'

        if (response.status === 409) {
          // 409 just means that the user is already registered.
          waitlistRequestResult = 'email-already-registered'
        } else if (!response.ok) {
          waitlistRequestResult = 'unexpected-error'
          reportClientError({
            error: new Error('Waitlist request failed'),
            context: {
              scope: 'useWaitlistService sendWaitlistRequest',
              label: '!response.ok',
            },
          })
        }

        return waitlistRequestResult
      } catch (error) {
        reportClientError({
          error,
          context: {
            scope: 'useWaitlistService sendWaitlistRequest',
            label: 'Waitlist request failed',
          },
        })

        return 'unexpected-error'
      }
    },
    []
  )

  return {
    clearMessages,
    closeWaitlistModal,
    currentColorKey,
    errorMessage,
    openWaitlistModal,
    productTitle,
    selectedVariant,
    sendWaitlistRequest,
    setErrorMessage,
    setSuccessMessage,
    shouldDisplayWaitlistModal,
    successMessage,
  }
}

export const WaitlistService = createContainer(useWaitlistService)
